import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axiosInstance from '../../shared/utils.js/axiosConfig';
import { BASE_URL, getToken, getCurrentLanguage } from '../../shared/utils.js/utils';

// Quotations Report
export const getQuotationsReport = createAsyncThunk(
  'projectReports/getQuotationsReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/quotations`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Contracts Report
export const getContractsReport = createAsyncThunk(
  'projectReports/getContractsReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/contracts`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Progress Bills Report
export const getProgressBillsReport = createAsyncThunk(
  'projectReports/getProgressBillsReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/progress-bills`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Tasks Report
export const getTasksReport = createAsyncThunk(
  'projectReports/getTasksReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/tasks`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Project Summary
export const getProjectSummary = createAsyncThunk(
  'projectReports/getProjectSummary',
  async ({ projectId, ...params } = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/${projectId}/summary`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Projects Overview
export const getProjectsOverview = createAsyncThunk(
  'projectReports/getProjectsOverview',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/reports/projects/overview`, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Projects (for dropdowns)
export const getProjectsForReport = createAsyncThunk(
  'projectReports/getProjectsForReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = params.language || getCurrentLanguage();
      const { language, ...restParams } = params; // Remove language from query params
      const response = await axiosInstance.get(`${BASE_URL}/projects`, {
        params: { per_page: 10, ...restParams },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Customers (for dropdowns)
export const getCustomersForProjectReport = createAsyncThunk(
  'projectReports/getCustomersForProjectReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/customers`, {
        params: { per_page: 10, ...params },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Contracts (for dropdowns)
export const getContractsForReport = createAsyncThunk(
  'projectReports/getContractsForReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/contracts`, {
        params: { per_page: 10, ...params },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Employees (for dropdowns)
export const getEmployeesForReport = createAsyncThunk(
  'projectReports/getEmployeesForReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/employees`, {
        params: { per_page: 1000, ...params },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Contractors (for dropdowns)
export const getContractorsForReport = createAsyncThunk(
  'projectReports/getContractorsForReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/contractors`, {
        params: { per_page: 1000, ...params },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

// Get Lab Tests (for dropdowns)
export const getLabTestsForReport = createAsyncThunk(
  'projectReports/getLabTestsForReport',
  async (params = {}, { rejectWithValue }) => {
    try {
      const token = getToken();
      const currentLang = getCurrentLanguage();
      const response = await axiosInstance.get(`${BASE_URL}/tests`, {
        params: { per_page: 1000, ...params },
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': currentLang,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  quotationsReport: null,
  contractsReport: null,
  progressBillsReport: null,
  tasksReport: null,
  projectSummary: null,
  projectsOverview: null,
  projects: [],
  customers: [],
  contracts: [],
  employees: [],
  contractors: [],
  labTests: [],
  projectsPagination: null,
  contractsPagination: null,
  customersPagination: null,
  isLoading: false,
  error: null,
  success: null,
};

const projectReportsSlice = createSlice({
  name: 'projectReports',
  initialState,
  reducers: {
    hydrate: (state, action) => {
      // Hydrate state from persisted storage
      if (action.payload) {
        return { ...state, ...action.payload };
      }
      return state;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearQuotationsReport: (state) => {
      state.quotationsReport = null;
    },
    clearContractsReport: (state) => {
      state.contractsReport = null;
    },
    clearProgressBillsReport: (state) => {
      state.progressBillsReport = null;
    },
    clearTasksReport: (state) => {
      state.tasksReport = null;
    },
    clearProjectSummary: (state) => {
      state.projectSummary = null;
    },
    clearProjectsOverview: (state) => {
      state.projectsOverview = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Quotations Report
      .addCase(getQuotationsReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getQuotationsReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationsReport = action.payload;
        state.success = 'Quotations report retrieved successfully';
      })
      .addCase(getQuotationsReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Contracts Report
      .addCase(getContractsReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getContractsReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.contractsReport = action.payload;
        state.success = 'Contracts report retrieved successfully';
      })
      .addCase(getContractsReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Progress Bills Report
      .addCase(getProgressBillsReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProgressBillsReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.progressBillsReport = action.payload;
        state.success = 'Progress bills report retrieved successfully';
      })
      .addCase(getProgressBillsReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Tasks Report
      .addCase(getTasksReport.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTasksReport.fulfilled, (state, action) => {
        state.isLoading = false;
        state.tasksReport = action.payload;
        state.success = 'Tasks report retrieved successfully';
      })
      .addCase(getTasksReport.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Project Summary
      .addCase(getProjectSummary.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProjectSummary.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projectSummary = action.payload;
        state.success = 'Project summary retrieved successfully';
      })
      .addCase(getProjectSummary.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Projects Overview
      .addCase(getProjectsOverview.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getProjectsOverview.fulfilled, (state, action) => {
        state.isLoading = false;
        state.projectsOverview = action.payload;
        state.success = 'Projects overview retrieved successfully';
      })
      .addCase(getProjectsOverview.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Get Projects
      .addCase(getProjectsForReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.projects = Array.isArray(data) ? data : [];
        state.projectsPagination = action.payload?.data?.meta || action.payload?.meta || null;
      })
      // Get Customers
      .addCase(getCustomersForProjectReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.customers = Array.isArray(data) ? data : [];
        state.customersPagination = action.payload?.data?.meta || action.payload?.meta || null;
      })
      // Get Contracts
      .addCase(getContractsForReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.contracts = Array.isArray(data) ? data : [];
        state.contractsPagination = action.payload?.data?.meta || action.payload?.meta || null;
      })
      // Get Employees
      .addCase(getEmployeesForReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.employees = Array.isArray(data) ? data : [];
      })
      // Get Contractors
      .addCase(getContractorsForReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.contractors = Array.isArray(data) ? data : [];
      })
      // Get Lab Tests
      .addCase(getLabTestsForReport.fulfilled, (state, action) => {
        const data = action.payload?.data?.data || action.payload?.data || action.payload;
        state.labTests = Array.isArray(data) ? data : [];
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearQuotationsReport,
  clearContractsReport,
  clearProgressBillsReport,
  clearTasksReport,
  clearProjectSummary,
  clearProjectsOverview,
} = projectReportsSlice.actions;

export default projectReportsSlice.reducer;
